import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { pluginApi, automationApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Zap, Plus, Trash2, Settings, GitBranch, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

interface AutomationAction {
  type: string;
  config: Record<string, any>;
}

interface AutomationEditor {
  name: string;
  description: string;
  trigger: {
    type: string;
    config: Record<string, any>;
  };
  conditions: Array<{ field: string; operator: string; value: any }>;
  actions: AutomationAction[];
  enabled: boolean;
  priority: number;
}

interface BlueprintRole {
  name: string;
  permissions: string[];
  mentionable?: boolean;
  hoist?: boolean;
}

interface BlueprintOverwrite {
  role: string;
  allow: string[];
  deny: string[];
}

interface BlueprintChannel {
  name: string;
  type: 'text' | 'voice' | 'forum' | 'stage';
  topic?: string;
  permissionOverwrites: BlueprintOverwrite[];
}

interface BlueprintCategory {
  name: string;
  permissionOverwrites: BlueprintOverwrite[];
  channels: BlueprintChannel[];
}

interface Blueprint {
  serverName?: string;
  roles: BlueprintRole[];
  categories: BlueprintCategory[];
}

interface BlueprintStructure {
  guild: {
    id: string;
    name: string;
    memberCount: number;
  };
  roles: Array<{
    id: string;
    name: string;
    managed: boolean;
    mentionable: boolean;
    hoist: boolean;
    position: number;
    permissions: string[];
  }>;
  categories: Array<{
    id: string;
    name: string;
    permissionOverwrites: BlueprintOverwrite[];
    channels: Array<{
      id: string;
      name: string;
      type: 'text' | 'voice' | 'forum' | 'stage';
      permissionOverwrites: BlueprintOverwrite[];
    }>;
  }>;
}

const TRIGGER_OPTIONS = [
  { value: 'member_join', label: 'Ingresso membro' },
  { value: 'member_leave', label: 'Uscita membro' },
  { value: 'message_sent', label: 'Messaggio inviato' },
  { value: 'reaction_add', label: 'Reazione aggiunta' },
  { value: 'timer', label: 'Timer programmato' },
  { value: 'command', label: 'Comando eseguito' },
  { value: 'voice_join', label: 'Ingresso vocale' },
  { value: 'voice_leave', label: 'Uscita vocale' },
];

const ACTION_OPTIONS = [
  { value: 'send_message', label: 'Invia messaggio' },
  { value: 'add_role', label: 'Aggiungi ruolo' },
  { value: 'remove_role', label: 'Rimuovi ruolo' },
  { value: 'ban_user', label: 'Banna utente' },
  { value: 'kick_user', label: 'Espelli utente' },
  { value: 'warn_user', label: 'Ammonisci utente' },
  { value: 'give_xp', label: 'Assegna XP' },
  { value: 'give_currency', label: 'Assegna valuta' },
  { value: 'run_command', label: 'Esegui comando' },
  { value: 'wait', label: 'Attendi' },
  { value: 'log', label: 'Scrivi log' },
];

function getTriggerLabel(value: string): string {
  return TRIGGER_OPTIONS.find((option) => option.value === value)?.label || value;
}

function getActionLabel(value: string): string {
  return ACTION_OPTIONS.find((option) => option.value === value)?.label || value;
}

const DEFAULT_AUTOMATION: AutomationEditor = {
  name: '',
  description: '',
  trigger: { type: 'member_join', config: {} },
  conditions: [],
  actions: [],
  enabled: true,
  priority: 0,
};

const DEFAULT_BLUEPRINT: Blueprint = {
  serverName: '',
  roles: [],
  categories: [],
};

const NONE_SELECT_VALUE = '__none__';

function csvToList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function listToCsv(items: string[] | undefined): string {
  return Array.isArray(items) ? items.join(', ') : '';
}

function TreeBranch({ isLast, children, className = '' }: { isLast: boolean; children: ReactNode; className?: string }) {
  return (
    <div className={`relative ml-2 pl-5 ${className}`}>
      <span className={`absolute left-0 top-0 border-l border-white/20 ${isLast ? 'h-4' : 'h-full'}`} />
      <span className="absolute left-0 top-4 w-4 border-t border-white/20" />
      {children}
    </div>
  );
}

export function AutomationsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: automations = [], refetch: refetchAutomations } = useQuery<any[]>({
    queryKey: ['servers', serverId, 'automations'],
    queryFn: async () => (await automationApi.getAutomations(serverId)) as any[],
  });

  const {
    data: structure,
    isLoading: structureLoading,
    refetch: refetchStructure,
  } = useQuery<BlueprintStructure>({
    queryKey: ['servers', serverId, 'automations', 'blueprint', 'structure'],
    queryFn: async () => (await automationApi.getBlueprintStructure(serverId)) as BlueprintStructure,
    enabled: true,
  });

  const automationsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'automations');
  const isEnabled = automationsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'automations') : pluginApi.disablePlugin(serverId, 'automations'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: savingConfig } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'automations', config),
    {
      onSuccess: () => toast.success('Configurazione salvata'),
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  const { mutateAsync: createAutomation, isPending: creatingAutomation } = useApiMutation(
    (payload: AutomationEditor) => automationApi.createAutomation(serverId, payload),
    { invalidateQueries: [['servers', serverId, 'automations']] }
  );

  const { mutateAsync: deleteAutomation, isPending: deletingAutomation } = useApiMutation(
    (automationId: string) => automationApi.deleteAutomation(serverId, automationId),
    { invalidateQueries: [['servers', serverId, 'automations']] }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    maxAutomationsPerServer: 50,
    allowUserAutomations: false,
    requireApproval: true,
  });

  const [showEditor, setShowEditor] = useState(false);
  const [automationEditor, setAutomationEditor] = useState<AutomationEditor>(DEFAULT_AUTOMATION);

  const [blueprint, setBlueprint] = useState<Blueprint>(DEFAULT_BLUEPRINT);
  const [blueprintGoals, setBlueprintGoals] = useState('');
  const [validatingBlueprint, setValidatingBlueprint] = useState(false);
  const [reviewingBlueprint, setReviewingBlueprint] = useState(false);
  const [applyingBlueprint, setApplyingBlueprint] = useState(false);
  const [blueprintValidation, setBlueprintValidation] = useState<any>(null);
  const [blueprintReview, setBlueprintReview] = useState<any>(null);
  const [blueprintApplyResult, setBlueprintApplyResult] = useState<any>(null);

  useEffect(() => {
    if (automationsPlugin?.config) {
      setConfig((prev: any) => ({ ...prev, ...automationsPlugin.config }));
    }
  }, [automationsPlugin]);

  const hasValidBlueprint = useMemo(() => !!blueprint && !!blueprint.serverName?.trim(), [blueprint]);

  const roleOptions = useMemo(
    () =>
      (structure?.roles || [])
        .filter((role) => !role.managed)
        .map((role) => ({ id: role.id, name: role.name })),
    [structure]
  );

  const channelOptions = useMemo(
    () =>
      (structure?.categories || []).flatMap((category) =>
        (category.channels || []).map((channel) => ({
          id: channel.id,
          name: channel.name,
          type: channel.type,
          categoryName: category.name,
        }))
      ),
    [structure]
  );

  const overwriteRoleOptions = useMemo(() => {
    const names = new Set<string>(['@everyone']);

    (structure?.roles || []).forEach((role) => {
      if (!role.managed && role.name?.trim()) {
        names.add(role.name.trim());
      }
    });

    (blueprint.roles || []).forEach((role) => {
      if (role.name?.trim()) {
        names.add(role.name.trim());
      }
    });

    return Array.from(names);
  }, [structure, blueprint.roles]);

  const updateRole = (index: number, next: Partial<BlueprintRole>) => {
    setBlueprint((prev) => ({
      ...prev,
      roles: prev.roles.map((role, idx) => (idx === index ? { ...role, ...next } : role)),
    }));
  };

  const addRole = () => {
    setBlueprint((prev) => ({
      ...prev,
      roles: [...prev.roles, { name: '', permissions: [], mentionable: false, hoist: false }],
    }));
  };

  const removeRole = (index: number) => {
    setBlueprint((prev) => ({
      ...prev,
      roles: prev.roles.filter((_, idx) => idx !== index),
    }));
  };

  const addCategory = () => {
    setBlueprint((prev) => ({
      ...prev,
      categories: [...prev.categories, { name: '', permissionOverwrites: [], channels: [] }],
    }));
  };

  const updateCategory = (categoryIndex: number, next: Partial<BlueprintCategory>) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) => (idx === categoryIndex ? { ...category, ...next } : category)),
    }));
  };

  const removeCategory = (categoryIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, idx) => idx !== categoryIndex),
    }));
  };

  const addCategoryOverwrite = (categoryIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              permissionOverwrites: [...category.permissionOverwrites, { role: '', allow: [], deny: [] }],
            }
          : category
      ),
    }));
  };

  const updateCategoryOverwrite = (categoryIndex: number, overwriteIndex: number, next: Partial<BlueprintOverwrite>) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              permissionOverwrites: category.permissionOverwrites.map((overwrite, ovIdx) =>
                ovIdx === overwriteIndex ? { ...overwrite, ...next } : overwrite
              ),
            }
          : category
      ),
    }));
  };

  const removeCategoryOverwrite = (categoryIndex: number, overwriteIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              permissionOverwrites: category.permissionOverwrites.filter((_, ovIdx) => ovIdx !== overwriteIndex),
            }
          : category
      ),
    }));
  };

  const addChannel = (categoryIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: [...category.channels, { name: '', type: 'text', topic: '', permissionOverwrites: [] }],
            }
          : category
      ),
    }));
  };

  const updateChannel = (categoryIndex: number, channelIndex: number, next: Partial<BlueprintChannel>) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: category.channels.map((channel, chIdx) => (chIdx === channelIndex ? { ...channel, ...next } : channel)),
            }
          : category
      ),
    }));
  };

  const removeChannel = (categoryIndex: number, channelIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: category.channels.filter((_, chIdx) => chIdx !== channelIndex),
            }
          : category
      ),
    }));
  };

  const addChannelOverwrite = (categoryIndex: number, channelIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: category.channels.map((channel, chIdx) =>
                chIdx === channelIndex
                  ? {
                      ...channel,
                      permissionOverwrites: [...channel.permissionOverwrites, { role: '', allow: [], deny: [] }],
                    }
                  : channel
              ),
            }
          : category
      ),
    }));
  };

  const updateChannelOverwrite = (
    categoryIndex: number,
    channelIndex: number,
    overwriteIndex: number,
    next: Partial<BlueprintOverwrite>
  ) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: category.channels.map((channel, chIdx) =>
                chIdx === channelIndex
                  ? {
                      ...channel,
                      permissionOverwrites: channel.permissionOverwrites.map((overwrite, ovIdx) =>
                        ovIdx === overwriteIndex ? { ...overwrite, ...next } : overwrite
                      ),
                    }
                  : channel
              ),
            }
          : category
      ),
    }));
  };

  const removeChannelOverwrite = (categoryIndex: number, channelIndex: number, overwriteIndex: number) => {
    setBlueprint((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) =>
        idx === categoryIndex
          ? {
              ...category,
              channels: category.channels.map((channel, chIdx) =>
                chIdx === channelIndex
                  ? {
                      ...channel,
                      permissionOverwrites: channel.permissionOverwrites.filter((_, ovIdx) => ovIdx !== overwriteIndex),
                    }
                  : channel
              ),
            }
          : category
      ),
    }));
  };

  const handleSaveConfig = () => {
    updateConfig(config);
  };

  const handleSaveAutomation = async () => {
    if (!automationEditor.name.trim()) {
      toast.error('Nome automazione obbligatorio');
      return;
    }

    if (automationEditor.actions.length === 0) {
      toast.error('Aggiungi almeno un’azione');
      return;
    }

    try {
      await createAutomation(automationEditor);
      toast.success('Automazione creata');
      setShowEditor(false);
      setAutomationEditor(DEFAULT_AUTOMATION);
      refetchAutomations();
    } catch (error: any) {
      toast.error(error?.message || 'Errore creazione automazione');
    }
  };

  const handleDeleteAutomation = async (automationId: string) => {
    if (!confirm('Eliminare questa automazione?')) return;
    try {
      await deleteAutomation(automationId);
      toast.success('Automazione eliminata');
      refetchAutomations();
    } catch (error: any) {
      toast.error(error?.message || 'Errore eliminazione automazione');
    }
  };

  const handleAddAction = () => {
    setAutomationEditor((prev) => ({
      ...prev,
      actions: [...prev.actions, { type: 'send_message', config: {} }],
    }));
  };

  const handleRemoveAction = (index: number) => {
    setAutomationEditor((prev) => ({
      ...prev,
      actions: prev.actions.filter((_, idx) => idx !== index),
    }));
  };

  const updateAction = (index: number, next: Partial<AutomationAction>) => {
    setAutomationEditor((prev) => {
      const nextActions = [...prev.actions];
      nextActions[index] = {
        ...nextActions[index],
        ...next,
        config: {
          ...(nextActions[index]?.config || {}),
          ...(next.config || {}),
        },
      };
      return { ...prev, actions: nextActions };
    });
  };

  const handleGenerateBlueprint = () => {
    if (!structure) return;

    const generated: Blueprint = {
      serverName: structure.guild.name,
      roles: structure.roles
        .filter((role) => !role.managed && role.name !== '@everyone')
        .map((role) => ({
          name: role.name,
          permissions: role.permissions,
          mentionable: role.mentionable,
          hoist: role.hoist,
        })),
      categories: structure.categories.map((category) => ({
        name: category.name,
        permissionOverwrites: category.permissionOverwrites,
        channels: category.channels.map((channel) => ({
          name: channel.name,
          type: channel.type,
          permissionOverwrites: channel.permissionOverwrites,
        })),
      })),
    };

    setBlueprint(generated);
    toast.success('Blueprint generato dalla struttura attuale');
  };

  const handleValidateBlueprint = async () => {
    setValidatingBlueprint(true);
    try {
      const result = await automationApi.validateBlueprint(serverId, blueprint);
      setBlueprintValidation(result);
      toast.success('Validazione completata');
    } catch (error: any) {
      toast.error(error?.message || 'Errore validazione blueprint');
    } finally {
      setValidatingBlueprint(false);
    }
  };

  const handleReviewBlueprint = async () => {
    setReviewingBlueprint(true);
    try {
      const result = (await automationApi.reviewBlueprint(serverId, blueprint, blueprintGoals || undefined)) as any;
      setBlueprintReview(result);
      if (!blueprintValidation && result?.validation) {
        setBlueprintValidation(result.validation);
      }
      toast.success('Review intelligente completata');
    } catch (error: any) {
      toast.error(error?.message || 'Errore review blueprint');
    } finally {
      setReviewingBlueprint(false);
    }
  };

  const handleApplyBlueprint = async (dryRun: boolean) => {
    setApplyingBlueprint(true);
    try {
      const result = (await automationApi.applyBlueprint(serverId, blueprint, dryRun)) as any;
      setBlueprintApplyResult(result?.result || null);
      if (result?.structure) {
        refetchStructure();
      }
      toast.success(dryRun ? 'Dry-run completata' : 'Blueprint applicato su Discord');
    } catch (error: any) {
      toast.error(error?.message || 'Errore apply blueprint');
    } finally {
      setApplyingBlueprint(false);
    }
  };

  if (!automationsPlugin) return null;

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Zap className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Automations</h3>
            <p className="text-sm text-muted-foreground">
              Configura automazioni e blueprint Discord in modo guidato.
            </p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-3">
            <h4 className="font-semibold">Percorso guidato (consigliato)</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="font-medium">1) Struttura</p>
                <p className="text-xs text-muted-foreground">Aggiorna struttura e genera il blueprint dal server attuale.</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="font-medium">2) Controllo</p>
                <p className="text-xs text-muted-foreground">Valida il blueprint per trovare mismatch di ruoli/canali/permessi.</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="font-medium">3) Simulazione</p>
                <p className="text-xs text-muted-foreground">Esegui “Simula Apply” per verificare cosa cambierebbe.</p>
              </div>
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="font-medium">4) Applica</p>
                <p className="text-xs text-muted-foreground">Applica il blueprint e poi crea le automazioni operative.</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-purple-400" />
              <h4 className="font-semibold">Impostazioni generali</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Max Automations Per Server</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={config.maxAutomationsPerServer}
                  onChange={(e) =>
                    setConfig({ ...config, maxAutomationsPerServer: Number.parseInt(e.target.value || '1', 10) || 1 })
                  }
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Automazioni utenti</Label>
                  <p className="text-xs text-muted-foreground">Permette agli utenti di creare automazioni</p>
                </div>
                <Switch
                  checked={config.allowUserAutomations}
                  onCheckedChange={(checked) => setConfig({ ...config, allowUserAutomations: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Richiede approvazione</Label>
                  <p className="text-xs text-muted-foreground">Richiede approvazione admin per automazioni utente</p>
                </div>
                <Switch
                  checked={config.requireApproval}
                  onCheckedChange={(checked) => setConfig({ ...config, requireApproval: checked })}
                />
              </div>
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Automazioni attive ({automations.length})</h4>
              <Button onClick={() => setShowEditor((prev) => !prev)} className="gap-2 bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4" />
                {showEditor ? 'Chiudi Editor' : 'Nuova Automazione'}
              </Button>
            </div>

            {automations.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessuna automazione configurata.</p>
            )}

            <div className="space-y-2">
              {(automations as any[]).map((automation: any) => (
                <div key={automation.id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{automation.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Trigger: {getTriggerLabel(automation.trigger?.type || 'n/a')} • Azioni: {Array.isArray(automation.actions) ? automation.actions.length : 0}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingAutomation}
                    onClick={() => handleDeleteAutomation(automation.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {showEditor && (
              <div className="p-4 rounded-lg border border-purple-500/30 bg-white/5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Nome</Label>
                    <Input
                      value={automationEditor.name}
                      onChange={(e) => setAutomationEditor((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Es: Welcome + Auto Role"
                    />
                  </div>
                  <div>
                    <Label>Priorità</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={automationEditor.priority}
                      onChange={(e) =>
                        setAutomationEditor((prev) => ({
                          ...prev,
                          priority: Number.parseInt(e.target.value || '0', 10) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label>Descrizione</Label>
                  <Input
                    value={automationEditor.description}
                    onChange={(e) => setAutomationEditor((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Cosa deve fare questa automazione"
                  />
                </div>

                <div>
                  <Label>Trigger</Label>
                  <Select
                    value={automationEditor.trigger.type}
                    onValueChange={(type) =>
                      setAutomationEditor((prev) => ({ ...prev, trigger: { ...prev.trigger, type } }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIGGER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Scegli l’evento che avvia automaticamente questo flusso.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Azioni</Label>
                    <Button size="sm" variant="outline" onClick={handleAddAction}>
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi azione
                    </Button>
                  </div>

                  {automationEditor.actions.map((action, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-white/10 bg-black/20 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Azione {idx + 1}: {getActionLabel(action.type)}</Label>
                        <Button size="sm" variant="destructive" onClick={() => handleRemoveAction(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <Select value={action.type} onValueChange={(value) => updateAction(idx, { type: value, config: {} })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTION_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {action.type === 'send_message' && (
                        <>
                          <Select
                            value={(() => {
                              const rawValue = String(action.config.channelId || action.config.channel || '').trim();
                              if (!rawValue) return NONE_SELECT_VALUE;
                              if (channelOptions.some((channel) => channel.id === rawValue)) return rawValue;
                              const byName = channelOptions.find((channel) => channel.name.toLowerCase() === rawValue.toLowerCase());
                              return byName?.id || NONE_SELECT_VALUE;
                            })()}
                            onValueChange={(value) =>
                              updateAction(idx, {
                                config: {
                                  channelId: value === NONE_SELECT_VALUE ? '' : value,
                                  channel: '',
                                },
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona canale dal server" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE_SELECT_VALUE}>Nessun canale</SelectItem>
                              {channelOptions.map((channel) => (
                                <SelectItem key={channel.id} value={channel.id}>
                                  #{channel.name} • {channel.categoryName} ({channel.type})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Testo messaggio"
                            value={action.config.content || ''}
                            onChange={(e) => updateAction(idx, { config: { content: e.target.value } })}
                          />
                        </>
                      )}

                      {(action.type === 'add_role' || action.type === 'remove_role') && (
                        <Select
                          value={(() => {
                            const rawValue = String(action.config.roleId || action.config.role || '').trim();
                            if (!rawValue) return NONE_SELECT_VALUE;
                            if (roleOptions.some((role) => role.id === rawValue)) return rawValue;
                            const normalized = rawValue.toLowerCase().replace(/^@/, '');
                            const byName = roleOptions.find((role) => role.name.toLowerCase() === normalized);
                            return byName?.id || NONE_SELECT_VALUE;
                          })()}
                          onValueChange={(value) =>
                            updateAction(idx, {
                              config: {
                                roleId: value === NONE_SELECT_VALUE ? '' : value,
                                role: '',
                              },
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona ruolo dal server" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_SELECT_VALUE}>Nessun ruolo</SelectItem>
                            {roleOptions.map((role) => (
                              <SelectItem key={role.id} value={role.id}>
                                @{role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {['ban_user', 'kick_user', 'warn_user'].includes(action.type) && (
                        <Input
                          placeholder="Reason"
                          value={action.config.reason || ''}
                          onChange={(e) => updateAction(idx, { config: { reason: e.target.value } })}
                        />
                      )}

                      {['give_xp', 'give_currency', 'wait'].includes(action.type) && (
                        <Input
                          type="number"
                          placeholder={action.type === 'wait' ? 'Delay ms' : 'Amount'}
                          value={action.config.amount ?? action.config.delay ?? ''}
                          onChange={(e) =>
                            updateAction(idx, {
                              config:
                                action.type === 'wait'
                                  ? { delay: Number.parseInt(e.target.value || '0', 10) || 0 }
                                  : { amount: Number.parseInt(e.target.value || '0', 10) || 0 },
                            })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <Label>Enabled</Label>
                  <Switch
                    checked={automationEditor.enabled}
                    onCheckedChange={(enabled) => setAutomationEditor((prev) => ({ ...prev, enabled }))}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowEditor(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleSaveAutomation} disabled={creatingAutomation} className="bg-purple-600 hover:bg-purple-700">
                    {creatingAutomation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}&nbsp;
                    Crea Automazione
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-purple-400" />
                <h4 className="font-semibold">Blueprint Discord (ruoli/categorie/canali/permessi)</h4>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => refetchStructure()} disabled={structureLoading}>
                  {structureLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiorna struttura'}
                </Button>
                <Button variant="outline" onClick={handleGenerateBlueprint} disabled={!structure}>
                  Genera da Discord
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Nome server blueprint</Label>
                <Input
                  value={blueprint.serverName || ''}
                  onChange={(e) => setBlueprint((prev) => ({ ...prev, serverName: e.target.value }))}
                  placeholder="Nome server"
                />
              </div>

              <div className="rounded-lg border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Albero blueprint modificabile</p>
                  <p className="text-xs text-muted-foreground">Categorie → Canali → Permessi • Ruoli globali</p>
                </div>

                <div className="space-y-4">
                  <div className="rounded-md border border-white/10 bg-white/5 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">🌐 Server: {blueprint.serverName || 'Senza nome'}</p>
                    </div>

                    <div className="mt-3 ml-2 border-l border-white/20 pl-4 space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-purple-200">Ruoli</p>
                          <Button size="sm" variant="outline" onClick={addRole}>
                            <Plus className="w-3 h-3 mr-1" />
                            Aggiungi ruolo
                          </Button>
                        </div>

                        <div className="space-y-2">
                          {blueprint.roles.map((role, roleIndex) => (
                            <TreeBranch
                              key={roleIndex}
                              isLast={roleIndex === blueprint.roles.length - 1}
                              className="p-2 rounded bg-white/5 space-y-2"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Input
                                  value={role.name}
                                  onChange={(e) => updateRole(roleIndex, { name: e.target.value })}
                                  placeholder="Nome ruolo"
                                />
                                <Input
                                  value={listToCsv(role.permissions)}
                                  onChange={(e) => updateRole(roleIndex, { permissions: csvToList(e.target.value) })}
                                  placeholder="Permessi (es: ViewChannel, SendMessages)"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">Nodo ruolo collegato al server</p>
                                <Button size="sm" variant="destructive" onClick={() => removeRole(roleIndex)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </TreeBranch>
                          ))}
                          {blueprint.roles.length === 0 && <p className="text-xs text-muted-foreground">Nessun ruolo nel blueprint.</p>}
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-purple-200">Categorie e canali</p>
                          <Button size="sm" variant="outline" onClick={addCategory}>
                            <Plus className="w-3 h-3 mr-1" />
                            Aggiungi categoria
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {blueprint.categories.map((category, categoryIndex) => (
                            <TreeBranch
                              key={categoryIndex}
                              isLast={categoryIndex === blueprint.categories.length - 1}
                              className="p-3 rounded bg-white/5 space-y-3"
                            >
                              <div className="flex items-center gap-2">
                                <Input
                                  value={category.name}
                                  onChange={(e) => updateCategory(categoryIndex, { name: e.target.value })}
                                  placeholder="Nome categoria"
                                />
                                <Button size="sm" variant="destructive" onClick={() => removeCategory(categoryIndex)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>

                              <div className="ml-2 border-l border-white/20 pl-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium text-muted-foreground">Permessi categoria</p>
                                  <Button size="sm" variant="outline" onClick={() => addCategoryOverwrite(categoryIndex)}>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Aggiungi permesso
                                  </Button>
                                </div>

                                {category.permissionOverwrites.map((overwrite, overwriteIndex) => (
                                  <TreeBranch
                                    key={overwriteIndex}
                                    isLast={overwriteIndex === category.permissionOverwrites.length - 1}
                                    className="p-2 rounded bg-black/20 grid grid-cols-1 md:grid-cols-4 gap-2"
                                  >
                                    <Select
                                      value={overwrite.role || NONE_SELECT_VALUE}
                                      onValueChange={(value) =>
                                        updateCategoryOverwrite(categoryIndex, overwriteIndex, {
                                          role: value === NONE_SELECT_VALUE ? '' : value,
                                        })
                                      }
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Ruolo" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value={NONE_SELECT_VALUE}>Nessun ruolo</SelectItem>
                                        {overwriteRoleOptions.map((roleName) => (
                                          <SelectItem key={roleName} value={roleName}>
                                            {roleName}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      value={listToCsv(overwrite.allow)}
                                      onChange={(e) =>
                                        updateCategoryOverwrite(categoryIndex, overwriteIndex, { allow: csvToList(e.target.value) })
                                      }
                                      placeholder="Allow (csv)"
                                    />
                                    <Input
                                      value={listToCsv(overwrite.deny)}
                                      onChange={(e) =>
                                        updateCategoryOverwrite(categoryIndex, overwriteIndex, { deny: csvToList(e.target.value) })
                                      }
                                      placeholder="Deny (csv)"
                                    />
                                    <Button size="sm" variant="destructive" onClick={() => removeCategoryOverwrite(categoryIndex, overwriteIndex)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </TreeBranch>
                                ))}
                              </div>

                              <div className="ml-2 border-l border-white/20 pl-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium text-muted-foreground">Canali</p>
                                  <Button size="sm" variant="outline" onClick={() => addChannel(categoryIndex)}>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Aggiungi canale
                                  </Button>
                                </div>

                                {category.channels.map((channel, channelIndex) => (
                                  <TreeBranch
                                    key={channelIndex}
                                    isLast={channelIndex === category.channels.length - 1}
                                    className="p-3 rounded bg-black/20 space-y-2"
                                  >
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                      <Input
                                        value={channel.name}
                                        onChange={(e) => updateChannel(categoryIndex, channelIndex, { name: e.target.value })}
                                        placeholder="Nome canale"
                                      />
                                      <Select
                                        value={channel.type}
                                        onValueChange={(type: 'text' | 'voice' | 'forum' | 'stage') =>
                                          updateChannel(categoryIndex, channelIndex, { type })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="text">text</SelectItem>
                                          <SelectItem value="voice">voice</SelectItem>
                                          <SelectItem value="forum">forum</SelectItem>
                                          <SelectItem value="stage">stage</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <Button size="sm" variant="destructive" onClick={() => removeChannel(categoryIndex, channelIndex)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>

                                    <Input
                                      value={channel.topic || ''}
                                      onChange={(e) => updateChannel(categoryIndex, channelIndex, { topic: e.target.value })}
                                      placeholder="Topic (opzionale)"
                                    />

                                    <div className="ml-2 border-l border-white/20 pl-3 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <p className="text-xs font-medium text-muted-foreground">Permessi canale</p>
                                        <Button size="sm" variant="outline" onClick={() => addChannelOverwrite(categoryIndex, channelIndex)}>
                                          <Plus className="w-3 h-3 mr-1" />
                                          Aggiungi permesso
                                        </Button>
                                      </div>

                                      {channel.permissionOverwrites.map((overwrite, overwriteIndex) => (
                                        <TreeBranch
                                          key={overwriteIndex}
                                          isLast={overwriteIndex === channel.permissionOverwrites.length - 1}
                                          className="p-2 rounded bg-black/30 grid grid-cols-1 md:grid-cols-4 gap-2"
                                        >
                                          <Select
                                            value={overwrite.role || NONE_SELECT_VALUE}
                                            onValueChange={(value) =>
                                              updateChannelOverwrite(categoryIndex, channelIndex, overwriteIndex, {
                                                role: value === NONE_SELECT_VALUE ? '' : value,
                                              })
                                            }
                                          >
                                            <SelectTrigger>
                                              <SelectValue placeholder="Ruolo" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value={NONE_SELECT_VALUE}>Nessun ruolo</SelectItem>
                                              {overwriteRoleOptions.map((roleName) => (
                                                <SelectItem key={roleName} value={roleName}>
                                                  {roleName}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <Input
                                            value={listToCsv(overwrite.allow)}
                                            onChange={(e) =>
                                              updateChannelOverwrite(categoryIndex, channelIndex, overwriteIndex, {
                                                allow: csvToList(e.target.value),
                                              })
                                            }
                                            placeholder="Allow (csv)"
                                          />
                                          <Input
                                            value={listToCsv(overwrite.deny)}
                                            onChange={(e) =>
                                              updateChannelOverwrite(categoryIndex, channelIndex, overwriteIndex, {
                                                deny: csvToList(e.target.value),
                                              })
                                            }
                                            placeholder="Deny (csv)"
                                          />
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => removeChannelOverwrite(categoryIndex, channelIndex, overwriteIndex)}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </TreeBranch>
                                      ))}
                                    </div>
                                  </TreeBranch>
                                ))}
                                {category.channels.length === 0 && <p className="text-xs text-muted-foreground">Nessun canale in categoria.</p>}
                              </div>
                            </TreeBranch>
                          ))}

                          {blueprint.categories.length === 0 && (
                            <p className="text-xs text-muted-foreground">Nessuna categoria nel blueprint.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Obiettivi (opzionale, per Gemini)</Label>
              <Textarea
                value={blueprintGoals}
                onChange={(e) => setBlueprintGoals(e.target.value)}
                className="min-h-[90px]"
                placeholder="Es: setup community tech con ruoli staff, support e canali forum con permessi separati"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleValidateBlueprint} disabled={validatingBlueprint || !hasValidBlueprint}>
                {validatingBlueprint ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valida Blueprint'}
              </Button>
              <Button onClick={handleReviewBlueprint} disabled={reviewingBlueprint || !hasValidBlueprint} variant="outline">
                {reviewingBlueprint ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Review intelligente (Gemini)'}
              </Button>
              <Button onClick={() => handleApplyBlueprint(true)} disabled={applyingBlueprint || !hasValidBlueprint} variant="outline">
                {applyingBlueprint ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simula modifiche'}
              </Button>
              <Button onClick={() => handleApplyBlueprint(false)} disabled={applyingBlueprint || !hasValidBlueprint}>
                {applyingBlueprint ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Applica su Discord'}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Suggerimento: usa prima “Simula modifiche”, poi “Applica su Discord”.
            </p>

            {blueprintValidation && (
              <div className="p-4 rounded-lg border border-white/10 bg-white/5 space-y-2">
                <p className="text-sm font-medium">
                  Validazione: {blueprintValidation.valid ? '✅ OK' : '⚠️ Da correggere'} • Score: {blueprintValidation.score}/100
                </p>
                {(blueprintValidation.issues || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun problema rilevato.</p>
                ) : (
                  <div className="space-y-1">
                    {(blueprintValidation.issues || []).slice(0, 12).map((issue: any, idx: number) => (
                      <p key={idx} className={`text-xs ${issue.level === 'error' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {issue.level.toUpperCase()}: {issue.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {blueprintReview?.aiReview && (
              <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-500/10 space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-purple-300" />
                  <p className="text-sm font-medium">Analisi AI ({blueprintReview.aiReview.provider})</p>
                </div>
                {blueprintReview.aiReview.summary && (
                  <p className="text-sm text-muted-foreground">{blueprintReview.aiReview.summary}</p>
                )}
                {(blueprintReview.aiReview.criticalRisks || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-300 mb-1">Rischi critici</p>
                    {(blueprintReview.aiReview.criticalRisks || []).slice(0, 8).map((risk: string, idx: number) => (
                      <p key={idx} className="text-xs text-red-300">• {risk}</p>
                    ))}
                  </div>
                )}
                {(blueprintReview.aiReview.recommendations || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-purple-200 mb-1">Raccomandazioni</p>
                    {(blueprintReview.aiReview.recommendations || []).slice(0, 10).map((rec: string, idx: number) => (
                      <p key={idx} className="text-xs text-purple-100">• {rec}</p>
                    ))}
                  </div>
                )}
                {(blueprintReview.aiReview.automationIdeas || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-green-300 mb-1">Idee automazioni</p>
                    {(blueprintReview.aiReview.automationIdeas || []).slice(0, 8).map((idea: string, idx: number) => (
                      <p key={idx} className="text-xs text-green-300">• {idea}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {blueprintApplyResult && (
              <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10 space-y-2">
                <p className="text-sm font-medium">
                  Apply {blueprintApplyResult?.dryRun ? '(dry-run)' : '(eseguito)'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Ruoli creati: {blueprintApplyResult?.summary?.rolesCreated ?? 0} • Ruoli aggiornati: {blueprintApplyResult?.summary?.rolesUpdated ?? 0} •
                  Categorie create: {blueprintApplyResult?.summary?.categoriesCreated ?? 0} • Canali creati: {blueprintApplyResult?.summary?.channelsCreated ?? 0} •
                  Overwrites aggiornati: {blueprintApplyResult?.summary?.overwritesUpdated ?? 0}
                </p>

                {Array.isArray(blueprintApplyResult?.details?.warnings) && blueprintApplyResult.details.warnings.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-yellow-300 mb-1">Warning</p>
                    {blueprintApplyResult.details.warnings.slice(0, 8).map((warning: string, idx: number) => (
                      <p key={idx} className="text-xs text-yellow-200">• {warning}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold">Albero struttura Discord corrente</h4>

            {structureLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Caricamento struttura...
              </div>
            )}

            {structure && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm font-medium">Server: {structure.guild.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Membri: {structure.guild.memberCount} • Ruoli: {structure.roles.length} • Categorie: {structure.categories.length}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Ruoli</p>
                  <div className="flex flex-wrap gap-2">
                    {structure.roles.map((role) => (
                      <span key={role.id} className="text-xs px-2 py-1 rounded bg-white/10 border border-white/10">
                        {role.name}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Categorie e Canali</p>
                  {structure.categories.map((category) => (
                    <div key={category.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-sm font-semibold">📁 {category.name}</p>
                      {category.channels.length === 0 ? (
                        <p className="text-xs text-muted-foreground mt-1">Nessun canale</p>
                      ) : (
                        <div className="mt-2 space-y-1">
                          {category.channels.map((channel) => (
                            <p key={channel.id} className="text-xs text-muted-foreground">
                              • #{channel.name} ({channel.type}) • overwrites: {channel.permissionOverwrites.length}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={handleSaveConfig} disabled={savingConfig} className="gap-2">
              {savingConfig ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salva Configurazione
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
